import { type Address, mapToAddressType } from "@/shared/@types/Address";
import type {
  AccountEntityExpandResponse,
  PickupsResponse,
} from "@/shared/generated/ExpHubApi";
import { getUniqueId } from "@/shared/helper/uniqueId";
import { mapToCountryCode, type CountryCode } from "./CountryCode";
import {
  mapToVehicleCategoryType,
  type VehicleCategory,
} from "./VehicleCategory";
import {
  mapToUnloadingSupportType,
  type UnloadingSupport,
} from "./UnloadingSupport";

export type AccountAddress = Address & {
  addressId: string;
  default: boolean;
  salesOrganizationId?: string;
  recipientEmail?: string;
  recipientPhone?: string;
  openingHours?: NonNullable<
    AccountEntityExpandResponse["addresses"]
  >[number]["openingHours"];
  vehicleCategory?: VehicleCategory;
  unloadingSupport?: UnloadingSupport;
  additionalDeliveryInfo?: string;
  countryCode?: CountryCode;
};

export const mapToAccountAddressType = (
  data:
    | NonNullable<AccountEntityExpandResponse["addresses"]>[number]
    | PickupsResponse["addresses"][number],
): AccountAddress => {
  const hasDefault =
    data && "default" in data && typeof data.default === "boolean";
  const hasRecipient = data && "recipient" in data && data.recipient;
  const hasOpeningHours = data && "openingHours" in data && data.openingHours;
  const hasRestrictions = data && "restrictions" in data && data.restrictions;
  const hasAddressInfo = data && "addressInfo3" in data && data.addressInfo3;

  return {
    ...mapToAddressType(data),
    addressId: data.addressId || getUniqueId("account-address"),
    default: hasDefault ? data.default : false,
    salesOrganizationId: data.salesOrganizationId,
    recipientEmail: hasRecipient ? data.recipient?.email : undefined,
    recipientPhone: hasRecipient ? data.recipient?.phone : undefined,
    openingHours: hasOpeningHours
      ? data.openingHours?.filter(
          (entry) =>
            !(entry.startTime === "00:00:00" && entry.endTime === "00:00:00"), // "00:00:00" is the default if no value was set by the user. We do not want to include such entries
        )
      : undefined,
    vehicleCategory: hasRestrictions
      ? mapToVehicleCategoryType(data.restrictions?.vehicle)
      : undefined,
    unloadingSupport: hasRestrictions
      ? mapToUnloadingSupportType(data.restrictions?.forklift)
      : undefined,
    additionalDeliveryInfo:
      hasAddressInfo && data.addressInfo3 !== "000000"
        ? data.addressInfo3
        : undefined, // "000000" is set as a default if no info is provided by the user
    countryCode: data.countryCode
      ? mapToCountryCode(data.countryCode)
      : undefined,
  };
};
